import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography, Box, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Radio, RadioGroup, FormControlLabel, FormControl, Alert, useMediaQuery, useTheme
} from '@mui/material';
import NfcIcon from '@mui/icons-material/Nfc';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BackspaceIcon from '@mui/icons-material/Backspace';
import Divider from '@mui/material/Divider';
import supabase from '../../supabaseClient';
import MachineProductionStats from './MachineProductionStats';

// ── Fullscreen scanner state overlay ─────────────────────────────────────────
function ScanOverlay({ state, msg }) {
  if (!state) return null;
  const isSuccess = state === 'success';
  const ringColor = isSuccess ? '#4caf50' : '#c62828';
  const iconBg    = isSuccess ? 'rgba(76,175,80,0.15)' : 'rgba(198,40,40,0.12)';
  const textColor = isSuccess ? '#1b5e20' : '#7f0000';
  const title     = isSuccess ? 'Skanowanie poprawne!' : 'Błąd odczytu!';
  const subtitle  = isSuccess ? 'Miłego dnia.' : 'Skontaktuj się z przełożonym.';

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1200,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: isSuccess
        ? 'radial-gradient(ellipse at center, #d0f0e0 0%, #b8e8cc 100%)'
        : 'radial-gradient(ellipse at center, #f5d0d0 0%, #e8b8b8 100%)',
      animation: 'fadeIn 0.3s ease',
      '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    }}>
      <Box sx={{
        width: 140, height: 140, borderRadius: '50%',
        bgcolor: iconBg,
        border: `3px solid ${ringColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: 4,
      }}>
        {isSuccess
          ? <CheckIcon sx={{ fontSize: 72, color: ringColor }} />
          : <CloseIcon sx={{ fontSize: 72, color: ringColor }} />}
      </Box>
      <Typography variant="h3" sx={{ fontWeight: 800, color: textColor, mb: 1 }}>{title}</Typography>
      <Typography variant="h6" sx={{ color: textColor, fontWeight: 400 }}>{subtitle}</Typography>
      {msg && <Typography variant="body2" sx={{ color: textColor, mt: 1, px: 4, textAlign: 'center' }}>{msg}</Typography>}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function WorkerDeviceAttendanceScanner() {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [idConfig,     setIdConfig]     = useState(null);
  const [deviceName,   setDeviceName]   = useState('');
  const [isScanning,   setIsScanning]   = useState(false);
  const [overlay,      setOverlay]      = useState(null);
  const [overlayMsg,   setOverlayMsg]   = useState('');
  const [activeWorkers,setActiveWorkers]= useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [clock,        setClock]        = useState('');
  const [pinInput,     setPinInput]     = useState('');
  const [pinError,     setPinError]     = useState('');

  // Stanowiska dostępne na tej maszynie
  const [deviceStands, setDeviceStands] = useState([]);
  
  // Stan dialogu wyboru stanowiska
  const [standDialogOpen, setStandDialogOpen] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [pendingAttendance, setPendingAttendance] = useState(null);
  const [selectedStandId, setSelectedStandId] = useState('');

  // Clock
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Init
  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data } = await supabase.from('profiles').select('id_configuration').eq('id', session.user.id).single();
        if (data) setIdConfig(data.id_configuration);
      }
      if (id) {
        const { data: device } = await supabase.from('devices').select('name').eq('id', id).maybeSingle();
        if (device) setDeviceName(device.name);
      }
      startNfc();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { if (id && selectedDate) fetchDeviceLogs(id, selectedDate); }, [id, selectedDate]);

  const showOverlay = (type, msg = '') => {
    setOverlay(type);
    setOverlayMsg(msg);
    setTimeout(() => { setOverlay(null); setOverlayMsg(''); }, 3000);
  };

  const fetchDeviceLogs = async (devId, date) => {
    const { data } = await supabase.from('device_attendance')
      .select('*, employees(name), device_stands(name)')
      .eq('device_id', devId)
      .gte('timestamp', `${date}T00:00:00Z`)
      .lte('timestamp', `${date}T23:59:59Z`)
      .order('timestamp', { ascending: false });

    if (data) { updateActiveWorkers(data); }
  };

  const updateActiveWorkers = (logs) => {
    const map = {};
    [...logs].reverse().forEach(log => {
      if (log.employees) {
        const standName = log.device_stands?.name || '';
        map[log.employee_id] = { 
          name: `${log.employees.name}`, 
          stand: standName,
          type: log.type 
        };
      }
    });

    const activeList = Object.values(map)
      .filter(w => w.type === 'START_WORK')
      .map(w => w.stand ? `${w.name} (${w.stand})` : w.name);

    setActiveWorkers(activeList);
  };

  const playSuccessSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(660, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(); osc.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  };

  const playClickSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); 
      const gain = ctx.createGain();
      
      osc.connect(gain); 
      gain.connect(ctx.destination);
      
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
      
      osc.start(); 
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.error("Audio error:", e);
    }
  };

  const startNfc = async () => {
    if (!('NDEFReader' in window)) { return; }
    try {
      const ndef = new window.NDEFReader();
      await ndef.scan();
      setIsScanning(true);
      ndef.onreading = ({ serialNumber }) => handleDeviceRecord(serialNumber);
    } catch (e) {}
  };

  const prepareDeviceLogging = async (emp) => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];

    const { data: attendanceRecord } = await supabase.from('attendance')
      .select('id, check_in, check_out, type')
      .eq('employee_id', emp.id).eq('type', 'work')
      .gte('check_in', `${todayStr}T00:00:00Z`)
      .lte('check_in', `${todayStr}T23:59:59Z`)
      .order('check_in', { ascending: false }).limit(1).maybeSingle();

    if (!attendanceRecord || attendanceRecord.check_out) {
      throw new Error('Nie potwierdzono obecności na wejściu głównym!');
    }

    const { data: lastDeviceRec } = await supabase.from('device_attendance')
      .select('timestamp').eq('employee_id', emp.id).eq('device_id', id)
      .order('timestamp', { ascending: false }).limit(1).maybeSingle();

    if (lastDeviceRec) {
      const diff = (Date.now() - new Date(lastDeviceRec.timestamp).getTime()) / 1000;
      if (diff < 60) throw new Error(`Odczekaj jeszcze ${Math.ceil(60 - diff)} sek.`);
    }

    const { data: lastAction } = await supabase.from('device_attendance')
      .select('type, stand_id').eq('employee_id', emp.id).eq('device_id', id)
      .order('timestamp', { ascending: false }).limit(1).maybeSingle();

    const nextType = (lastAction?.type === 'START_WORK') ? 'STOP_WORK' : 'START_WORK';

    if (nextType === 'START_WORK') {
      const { data: standsData, error: standsError } = await supabase
        .from('device_stands')
        .select('id, name')
        .eq('device_id', id)
        .eq('id_configuration', idConfig)
        .order('name', { ascending: true });

      if (standsError) {
        throw new Error('Błąd pobierania stanowisk maszyny.');
      }

      if (!standsData || standsData.length === 0) {
        throw new Error('Brak zdefiniowanych stanowisk dla tej maszyny!');
      }

      setDeviceStands(standsData);
      setPendingEmployee(emp);
      setPendingAttendance(attendanceRecord);
      setSelectedStandId(String(standsData[0].id));
      setStandDialogOpen(true);
      return null;
    } else {
      const lastStandId = lastAction?.stand_id || null;
      return await executeSaveDeviceAttendance(emp, attendanceRecord.id, nextType, lastStandId);
    }
  };

  const executeSaveDeviceAttendance = async (emp, attendanceId, type, standId) => {
    const now = new Date();

    const payload = {
      employee_id: emp.id,
      device_id: id,
      attendance_id: attendanceId,
      type: type,
      timestamp: now.toISOString(),
      id_configuration: idConfig,
      ...(standId && { stand_id: Number(standId) })
    };

    await supabase.from('device_attendance').insert([payload]);

    playSuccessSound();
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    const actionMsg = type === 'START_WORK' ? 'Rozpoczęto pracę' : 'Zakończono pracę';
    return `${actionMsg}: ${emp.name}`;
  };

  const handleConfirmStandChoice = async () => {
    if (!pendingEmployee || !pendingAttendance) return;

    try {
      const msg = await executeSaveDeviceAttendance(
        pendingEmployee,
        pendingAttendance.id,
        'START_WORK',
        selectedStandId
      );
      setStandDialogOpen(false);
      setPendingEmployee(null);
      setPendingAttendance(null);
      setPinInput('');
      showOverlay('success', msg);
      fetchDeviceLogs(id, selectedDate);
    } catch (e) {
      setStandDialogOpen(false);
      showOverlay('error', e.message || 'Błąd zapisu stanowiska');
    }
  };

  const handleDeviceRecord = async (serial) => {
    const cleanSerial = serial.toLowerCase().trim();
    try {
      const { data: emp } = await supabase.from('employees')
        .select('id, name, id_configuration').eq('nfc_card_id', cleanSerial).maybeSingle();
      if (!emp || emp.id_configuration !== idConfig) { showOverlay('error', 'Nieznana karta'); return; }
      
      const msg = await prepareDeviceLogging(emp);
      if (msg) {
        showOverlay('success', msg);
        fetchDeviceLogs(id, selectedDate);
      }
    } catch (e) { showOverlay('error', e.message || 'Błąd zapisu'); }
  };

  const handleDeviceRecordByPin = async (typedPin) => {
    if (!typedPin) return;
    setPinError('');
    try {
      const { data: emp, error } = await supabase.from('employees')
        .select('id, name, id_configuration').eq('pin', typedPin.trim()).maybeSingle();
      if (error || !emp || emp.id_configuration !== idConfig) {
        setPinError('Nieprawidłowy kod PIN!');
        setPinInput('');
        return;
      }
      const msg = await prepareDeviceLogging(emp);
      if (msg) {
        showOverlay('success', msg);
        setPinInput('');
        fetchDeviceLogs(id, selectedDate);
      }
    } catch (e) {
      setPinError(e.message || 'Błąd autoryzacji');
      setPinInput('');
    }
  };

  const handleKeyPress = (num) => { 
    playClickSound(); 
    setPinError(''); 
    if (pinInput.length < 8) setPinInput(p => p + num); 
  };
  
  const handleBackspace = () => {
    playClickSound(); 
    setPinInput(p => p.slice(0, -1));
  };

  // Komponent z klawiaturą PIN do reużycia w wybranym miejscu
  const PinPanel = () => (
    <Box sx={{ width: '100%', maxWidth: 320, mx: 'auto' }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>Wprowadź kod PIN</Typography>
      </Box>

      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="h3" sx={{
          letterSpacing: '10px', fontWeight: 700, minHeight: 56,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          bgcolor: '#f5f5f5', borderRadius: 3, border: '1px solid #e0e0e0'
        }}>
          {'•'.repeat(pinInput.length)}
        </Typography>
        {pinError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 3 }}>{pinError}</Alert>}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <Button key={n} fullWidth variant="contained" color="inherit"
            onClick={() => handleKeyPress(n.toString())}
            sx={{ py: isMobile ? 2 : 2.5, fontSize: '1.6rem', fontWeight: 700, borderRadius: 3, bgcolor: '#fafafa', boxShadow: 1 }}>
            {n}
          </Button>
        ))}
        <Button fullWidth variant="outlined" color="error" onClick={handleBackspace} sx={{ py: isMobile ? 2 : 2.5, borderRadius: 3 }}>
          <BackspaceIcon />
        </Button>
        <Button fullWidth variant="contained" color="inherit" onClick={() => handleKeyPress('0')}
          sx={{ py: isMobile ? 2 : 2.5, fontSize: '1.6rem', fontWeight: 700, borderRadius: 3, bgcolor: '#fafafa', boxShadow: 1 }}>
          0
        </Button>
        <Button fullWidth variant="contained" color="success" onClick={() => handleDeviceRecordByPin(pinInput)}
          disabled={pinInput.length === 0}
          sx={{ py: isMobile ? 2 : 2.5, fontSize: '1.1rem', fontWeight: 700, borderRadius: 3 }}>
          OK
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: 'transparent', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <ScanOverlay state={overlay} msg={overlayMsg} />

      {/* ────────────────── UKŁAD MOBILNY ────────────────── */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2, pb: 2, px: 2, gap: 3, maxWidth: 500, mx: 'auto', width: '100%' }}>
          {/* 1. Nazwa maszyny i zegar */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 0.2 }}>
              {deviceName || 'Stanowisko maszynowe'}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 300, color: '#444', letterSpacing: 3 }}>
              {clock}
            </Typography>
          </Box>

          {/* 2. Klawiatura PIN */}
          <PinPanel />

          {/* 3. Statystyki maszyny */}
          <Box sx={{ width: '100%' }}>
            <MachineProductionStats
              deviceId={id}
              idConfiguration={idConfig}
              deviceName={deviceName}
              clock={clock}
              activeWorkers={activeWorkers}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
          </Box>
        </Box>
      ) : (
        /* ────────────────── UKŁAD DESKTOP / TABLET ────────────────── */
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          maxWidth: 1300,
          mx: 'auto',
          pt: 3,
          pb: 2,
          px: 2
        }}>
          {/* Lewa strona: Statystyki — więcej przestrzeni niż PIN, który ma stałą,
              węższą szerokość (patrz PinPanel maxWidth) i jej nie potrzebuje */}
          <Box sx={{ flex: '1 1 680px', minWidth: 0 }}>
            <MachineProductionStats
              deviceId={id}
              idConfiguration={idConfig}
              deviceName={deviceName}
              clock={clock}
              activeWorkers={activeWorkers}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
          </Box>

          {/* Pionowy separator */}
          <Divider orientation="vertical" flexItem sx={{ mx: 3, my: 4 }} />

          {/* Prawa strona: Klawiatura PIN */}
          <Box sx={{ flex: '0 0 320px' }}>
            <PinPanel />
          </Box>
        </Box>
      )}

      {/* ── Dialog wyboru stanowiska ── */}
      <Dialog 
        open={standDialogOpen} 
        onClose={() => setStandDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
          Wybierz stanowisko pracy
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Pracownik: <strong>{pendingEmployee?.name}</strong>
          </Typography>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup
              value={selectedStandId}
              onChange={(e) => setSelectedStandId(e.target.value)}
            >
              {deviceStands.map((stand) => (
                <FormControlLabel
                  key={stand.id}
                  value={String(stand.id)}
                  control={<Radio />}
                  label={stand.name}
                  sx={{
                    mb: 1, p: 1, border: '1px solid #eee', borderRadius: 2, width: '100%', mx: 0,
                    bgcolor: selectedStandId === String(stand.id) ? '#f0f7ff' : 'transparent',
                    borderColor: selectedStandId === String(stand.id) ? '#1976d2' : '#eee'
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setStandDialogOpen(false)} 
            color="inherit" 
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleConfirmStandChoice} 
            variant="contained" 
            color="primary"
            disabled={!selectedStandId}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 2 }}
          >
            Zatwierdź
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default WorkerDeviceAttendanceScanner;